import { MouseEvent, KeyboardEvent, useState, useRef } from 'react'
import { Pencil, Trash2Icon } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import type { Workspace } from '@/service/api'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import InlineRenameInput from '@/components/InlineRenameInput'
import RowActionsDropdown from '@/components/DataTable/RowActionsDropdown'
import { RowAction } from '@/components/DataTable/DataTableTypes'
import { useAppStore } from '@/store'
import { getUserInfo, getUserInitials } from '@/utils/userUtils'
import { formatDate } from '@/utils/timeUtils'
import { useTranslation } from 'react-i18next'

// Constants for non-translatable strings
const CONSTANTS = {
  EMAIL_SUFFIX: '@intuigence.ai',
} as const

interface BoardTableProps {
  boards: Workspace[]
  onBoardClick: (id: string) => void
  onBoardDelete: (id: string) => void
  loading?: boolean
  selectable?: boolean
  selectedRows?: string[]
  onRowSelect?: (id: string, checked: boolean) => void
  onSelectAll?: (checked: boolean) => void
}

const BoardTable = ({
  boards,
  onBoardClick,
  onBoardDelete,
  loading = false,
  selectable = false,
  selectedRows = [],
  onRowSelect,
  onSelectAll,
}: BoardTableProps) => {
  const { t } = useTranslation()
  const { user, renameBoard } = useAppStore()
  const [renamingBoard, setRenamingBoard] = useState<{ id: string; name: string } | null>(null)

  // Get menu actions for a board
  const getMenuActions = (board: Workspace): RowAction[] => {
    return [
      {
        name: t('board:actions.rename_board'),
        file_type: ['board'],
        icon: <Pencil className="w-4 h-4" />,
        action: (
          e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
        ) => {
          e.stopPropagation()
          setRenamingBoard({ id: board.id, name: board.name })
        },
      },
      {
        name: t('common:buttons.delete'),
        file_type: ['board'],
        icon: <Trash2Icon className="w-4 h-4" />,
        action: (
          e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
          id: string
        ) => {
          e.stopPropagation()
          onBoardDelete(id)
        },
      },
    ]
  }

  const renameSavingRef = useRef(false)

  const handleRenameSave = () => {
    if (renameSavingRef.current) return
    renameSavingRef.current = true

    const trimmed = renamingBoard?.name.trim()
    if (trimmed && renamingBoard) {
      renameBoard(renamingBoard.id, trimmed)
    }
    setRenamingBoard(null)
    setTimeout(() => { renameSavingRef.current = false }, 0)
  }

  const handleRenameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleRenameSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setRenamingBoard(null)
    }
  }

  return (
    <div className="bg-white dark:bg-dark-card rounded-lg border border-gray-200 dark:border-dark-border overflow-hidden mb-5">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50 dark:bg-dark-muted">
            {selectable && (
              <TableHead className="w-12 ps-4">
                <Checkbox
                  checked={
                    boards.length > 0 &&
                    boards.every(b => selectedRows.includes(b.id))
                      ? true
                      : boards.some(b => selectedRows.includes(b.id))
                        ? 'indeterminate'
                        : false
                  }
                  onCheckedChange={(checked) => onSelectAll?.(checked === true)}
                />
              </TableHead>
            )}
            <TableHead className="font-semibold ps-4">
              {t('board:table.board_name')}
            </TableHead>
            <TableHead className="font-semibold">
              {t('board:table.created_by')}
            </TableHead>
            <TableHead className="font-semibold">
              {t('board:table.last_modified')}
            </TableHead>
            <TableHead className="font-semibold">
              {t('board:table.created')}
            </TableHead>
            <TableHead className="w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={selectable ? 6 : 5} className="text-center py-8">
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                  <span className="ms-2 text-gray-500 dark:text-dark-text-muted">
                    {t('board:table.loading')}
                  </span>
                </div>
              </TableCell>
            </TableRow>
          ) : boards.length === 0 ? (
            <TableRow>
              <TableCell colSpan={selectable ? 6 : 5} className="text-center py-8 text-gray-500 dark:text-dark-text-muted">
                {t('board:table.no_boards')}
              </TableCell>
            </TableRow>
          ) : (
            boards.map((board) => {
              const userInfo = getUserInfo(
                board.user_id,
                user,
                CONSTANTS.EMAIL_SUFFIX
              )
              return (
                <TableRow
                  key={board.id}
                  className="hover:bg-gray-50 dark:hover:bg-dark-muted px-20 cursor-pointer"
                  onClick={() => onBoardClick(board.id)}
                >
                  {selectable && (
                    <TableCell className="ps-4" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedRows.includes(board.id)}
                        onCheckedChange={(checked) => onRowSelect?.(board.id, checked === true)}
                      />
                    </TableCell>
                  )}
                  <TableCell className="font-medium ps-4" onClick={renamingBoard?.id === board.id ? (e) => e.stopPropagation() : undefined}>
                    {renamingBoard?.id === board.id ? (
                      <div className="flex items-center gap-2">
                        {/* Same typography + wrapper as the display span, so
                            the row keeps its exact footprint while renaming. */}
                        <InlineRenameInput
                          value={renamingBoard.name}
                          onChange={(e) => setRenamingBoard((prev) => prev ? { ...prev, name: e.target.value } : null)}
                          onKeyDown={handleRenameKeyDown}
                          onBlur={handleRenameSave}
                          autoFocus
                          className="text-sm truncate max-w-[18rem]"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-sm truncate max-w-[18rem]">{board.name}</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                        <span className="text-orange-600 font-semibold text-sm">
                          {getUserInitials(board.user_id, user)}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-dark-text">
                          {userInfo.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-dark-text-muted">
                          {userInfo.email}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600 dark:text-gray-400">
                    {formatDate(board.updated_at)}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600 dark:text-gray-400">
                    {formatDate(board.created_at)}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="me-3">
                      <RowActionsDropdown
                        id={board.id}
                        actions={getMenuActions(board)}
                        rowType="board"
                      />
                    </div>
                  </TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </div>
  )
}

export default BoardTable
